import type { MapView } from './model';

/** Canonical persisted longitude, retaining an already canonical +180. */
export function normalizedLongitude(longitude: number): number {
  if (longitude >= -180 && longitude <= 180) return longitude;
  return ((((longitude + 180) % 360) + 360) % 360) - 180;
}

/** Project the nearest copy without changing any authored geographic coordinates. */
export function nearestWorldLongitude(longitude: number, center: number): number {
  return longitude + 360 * Math.round((center - longitude) / 360);
}

/**
 * One-world ground footprint for MapLibre's flat Mercator camera (no terrain/roll).
 * Inverse perspective at four viewport corners includes bearing and the supported 0–60° pitch.
 * A 512px world at zoom zero grows by 2^zoom. This floor is independent of centre longitude.
 */
export function singleWorldMinimumZoom(
  width: number,
  height: number,
  bearing = 0,
  pitch = 0,
  verticalFieldOfView = 36.86989764584402,
): number {
  if (width <= 0 || height <= 0) return 0;
  const radians = Math.PI / 180;
  const angle = bearing * radians;
  const tilt = Math.min(60, Math.max(0, pitch)) * radians;
  const distance = height / (2 * Math.tan((verticalFieldOfView * radians) / 2));
  const xs: number[] = [];
  for (const y of [-height / 2, height / 2]) {
    const factor = distance / (distance - y * Math.tan(tilt));
    for (const x of [-width / 2, width / 2])
      xs.push((x * Math.cos(angle) + (y / Math.cos(tilt)) * Math.sin(angle)) * factor);
  }
  // Tiny margin prevents floating-point roundoff revealing the same meridian twice.
  // MapLibre's public setMinZoom accepts values from -2; tiny inset/preview frames can be smaller.
  return Math.max(-2, Math.log2(Math.max(height, Math.max(...xs) - Math.min(...xs)) / 512) + 1e-7);
}

/** Same polar/zoom constraints as the flat Mercator renderer; authored state is untouched. */
export function constrainedWorldView(view: MapView, width: number, height: number): MapView {
  if (width <= 0 || height <= 0) return view;
  const zoom = Math.max(view.zoom, singleWorldMinimumZoom(width, height, view.bearing, view.pitch));
  const latitude = Math.max(-85.0511287798066, Math.min(85.0511287798066, view.center[1]));
  const y = (1 - Math.log(Math.tan(Math.PI / 4 + (latitude * Math.PI) / 360)) / Math.PI) / 2;
  const inset = Math.min(0.5, height / (2 * 512 * 2 ** zoom));
  const effectiveY = Math.max(inset, Math.min(1 - inset, y));
  const effectiveLatitude =
    Math.abs(effectiveY - y) < 1e-12
      ? latitude
      : (Math.atan(Math.sinh(Math.PI * (1 - 2 * effectiveY))) * 180) / Math.PI;
  return { ...view, zoom, center: [normalizedLongitude(view.center[0]), effectiveLatitude] };
}
